import "./Topbar.scss";
import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { AuthContext } from "../../contexts/AuthContext";
import { Modal } from "../../components/Modal/Modal";
import { DynamicInput } from "../../components/Inputs/Inputs";
import AlertModal from "../AlertModal/AlertModal";
import { formatDate } from "../../utils/formatDate";
import { useLayout } from "../../contexts/LayoutContext";
import { ReactComponent as ChevronLeft } from "../../assets/chevron-left.svg";
import { ReactComponent as User } from "../../assets/user.svg";
import { ReactComponent as Home } from "../../assets/home.svg";
import { ReactComponent as Heart } from "../../assets/heart.svg";
import { ReactComponent as Upload } from "../../assets/upload.svg";
import { ReactComponent as Eye } from "../../assets/eye.svg";
import { ReactComponent as EyeOff } from "../../assets/eye-off.svg";
import { ReactComponent as Clock } from "../../assets/clock.svg";
import { ReactComponent as Pointer } from "../../assets/mouse-pointer.svg";
import { ReactComponent as Smile } from "../../assets/smile.svg";
import { ReactComponent as Bell } from "../../assets/bell.svg";
import { ReactComponent as Aperture } from "../../assets/aperture.svg";
import { HideChat } from "../HideChat/HideChat";
import { ReactComponent as Key } from "../../assets/key.svg";
import TwoFactorManage from "../TwoFactor/TwoFactorManage";

export const Topbar = () => {
  const {
    currentUser,
    axiosAuth,
    logout,
    accessibleGroups,
    accessibleHotels,
    accessLoading,
    token,
  } = useContext(AuthContext);
  const { collapsed, toggleSidebar } = useLayout();
  const [openMenu, setOpenMenu] = useState(null); // "profile" | "properties" | "null";
  const profileRef = useRef(null);
  const propertiesRef = useRef(null);
  const notificationsRef = useRef(null);
  const hideChatRef = useRef(null);

  const propertiesList = accessibleHotels || [];

  // profile & change password
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [successModal, setSuccessModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  // o componente dinâmico DynamicInput espera que a prop onChange seja uma função no formato:
  // function handleChange(name, value) {...}
  const handleInputChange = (name, value) => {
    if (name === "currentPassword") setCurrentPassword(value);
    if (name === "newPassword") setNewPassword(value);
    if (name === "confirmNewPassword") setConfirmNewPassword(value);
  };

  // fechar ao carregar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        profileRef.current?.contains(e.target) ||
        propertiesRef.current?.contains(e.target) ||
        notificationsRef.current?.contains(e.target) ||
        hideChatRef?.current?.contains(e.target)
      ) {
        return; // clicou dentro de um menu -> não fecha
      }
      setOpenMenu(null); // fecha se clicou fora
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpenMenu(null);
    };

    if (openMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenu]);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setErrorModal({
        open: true,
        title: "Validação",
        message: "Preencha todos os campos de palavra-passe.",
      });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorModal({
        open: true,
        title: "Validação",
        message: "As palavras-passe não coincidem.",
      });
      return;
    }
    if (newPassword.length < 8) {
      setErrorModal({
        open: true,
        title: "Validação",
        message: "A nova palavra-passe deve ter pelo menos 8 caracteres.",
      });
      return;
    }

    setSaving(true);

    try {
      await axiosAuth.put(`/users/me/password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccessModal({
        open: true,
        title: "Sucesso!",
        message: `A Palavra passe de ${currentUser.username} foi alterada com sucesso!`,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");

      // fecha o mdal depois de um delay
      setTimeout(() => setIsProfileModalOpen(false), 1500);
    } catch (err) {
      setErrorModal({
        open: true,
        title: "Ocorreu um erro.",
        message: `Erro ao atualizar password: ${
          err.response?.data?.detail || message
        }`,
      });
    } finally {
      setSaving(false);
    }
  };

  const isSuperAdmin = currentUser?.role === "superadmin";
  const navigate = useNavigate();

  return (
    <div className="topbar">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={() => setErrorModal({ open: false, message: "", title: "" })}
        />
      )}
      {successModal.open && (
        <AlertModal
          variant="success"
          title={successModal.title || "Sucesso!"}
          message={successModal.message}
          onClose={() =>
            setSuccessModal({ open: false, message: "", title: "" })
          }
        />
      )}
      <div className="topbar-container">
        <div className="left-side">
          <button
            className={`collapse-button ${collapsed ? "is-collapsed" : ""}`}
            onClick={toggleSidebar}
            aria-pressed={collapsed}
            aria-label={collapsed ? "Show Sidebar" : "Hide Sidebar"}
          >
            <ChevronLeft />
          </button>
          <button
            type="button"
            className="back-button"
            onClick={() => navigate(-1)}
            aria-label="Voltar"
          >
            <IoMdArrowBack size={16} />
            <span>Voltar</span>
          </button>
          <div className="my-group">
            {isSuperAdmin
              ? "Superadmin"
              : `${accessibleGroups[0]?.name ?? ""}`}
          </div>
        </div>
        <div className="right-side">
          {isSuperAdmin && (
            <div
              className="topicon hideChat"
              onClick={() =>
                setOpenMenu(openMenu === "hideChat" ? null : "hideChat")
              }
            >
              <Aperture />
            </div>
          )}
          {!isSuperAdmin && (
            <div
              className="topicon properties"
              onClick={() =>
                setOpenMenu(openMenu === "properties" ? null : "properties")
              }
            >
              <Home />
            </div>
          )}
          {!isSuperAdmin && (
            <div
              className="topicon notifications"
              onClick={() =>
                setOpenMenu(
                  openMenu === "notifications" ? null : "notifications"
                )
              }
            >
              <Bell />
            </div>
          )}
          <div
            className="topicon profile"
            onClick={() =>
              setOpenMenu(openMenu === "profile" ? null : "profile")
            }
          >
            <User />
          </div>
        </div>
        <div
          ref={profileRef}
          className={
            openMenu === "profile" ? "open-profile opened" : "open-profile"
          }
        >
          <div className="open-profile-info">
            <div className="open-profile-info-left">
              <div className="user-icon">
                <User />
              </div>
            </div>
            {currentUser && (
              <div className="open-profile-info-right">
                <p className="username">{currentUser.username}</p>
                <p className="email">{currentUser.email}</p>
              </div>
            )}
          </div>
          <div className="open-profile-menu">
            <div
              className="menu-item"
              onClick={() => setIsProfileModalOpen(true)}
            >
              <User />
              <span>Perfil</span>
            </div>
            <div
              className="menu-item"
              onClick={() => setIsSecurityModalOpen(true)}
            >
              <Key />
              <span>Segurança (2FA)</span>
            </div>
            <div className="menu-item" onClick={logout}>
              <Upload style={{ transform: "rotate(90deg)" }} />
              <span>Terminar sessão</span>
            </div>
          </div>
        </div>
        <div
          ref={propertiesRef}
          className={
            openMenu === "properties"
              ? "open-properties opened"
              : "open-properties"
          }
        >
          <div className="properties">
            {propertiesList.map((p) => (
              <div key={p.name} className="property">
                <Heart />
                <p>{p.name}</p>
              </div>
            ))}
          </div>
        </div>
        <div
          ref={notificationsRef}
          className={
            openMenu === "notifications"
              ? "open-notifications opened"
              : "open-notifications"
          }
        >
          <p>Sem notificações para mostrar.</p>
        </div>
        <div ref={hideChatRef} className="open-hide-chat-menu">
          <HideChat isOpen={openMenu === "hideChat"} />
        </div>
      </div>
      {currentUser && (
        <Modal
          isVisible={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          title="Perfil"
          saveText="Guardar"
          onSave={handleChangePassword}
        >
          <div className="row wicon">
            <div className="row-inner">
              {/* <h4>Nome de utilizador:</h4> */}
              <Smile />
              <p>{currentUser.username}</p>
            </div>
          </div>
          <div className="row wicon">
            <div className="row-inner">
              {/* <h4>Email:</h4> */}
              <Pointer />
              <p>{currentUser.email}</p>
            </div>
          </div>
          <div className="row wicon" style={{ marginBottom: "1rem" }}>
            <div className="row-inner">
              {/* <h4>Conta ativa desde:</h4> */}
              <Clock />
              <p>{formatDate(currentUser.created_at)}</p>
            </div>
          </div>
          <div className="row">
            <DynamicInput
              label="Palavra-passe atual"
              type={showPassword ? "text" : "password"}
              name="currentPassword"
              value={currentPassword}
              onChange={handleInputChange}
              required
            />
            <div
              className="show-hide-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff /> : <Eye />}
            </div>
          </div>
          <div className="row">
            <DynamicInput
              label="Nova palavra-passe"
              type={showPassword ? "text" : "password"}
              value={newPassword}
              name="newPassword"
              onChange={handleInputChange}
            />
            <div
              className="show-hide-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff /> : <Eye />}
            </div>
          </div>
          <div className="row">
            <DynamicInput
              label="Confirmar nova palavra-passe"
              type={showPassword ? "text" : "password"}
              name="confirmNewPassword"
              value={confirmNewPassword}
              onChange={handleInputChange}
            />
            <div
              className="show-hide-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff /> : <Eye />}
            </div>
          </div>
        </Modal>
      )}
      <Modal
        isVisible={isSecurityModalOpen}
        onClose={() => setIsSecurityModalOpen(false)}
        title="Segurança (2FA)"
      >
        {isSecurityModalOpen && (
          <TwoFactorManage axiosAuth={axiosAuth} sessionToken={token} />
        )}
      </Modal>
    </div>
  );
};
